/**
 * 通用 Marquee 动画工具
 * 支持无限滚动、响应式、可访问性
 * 
 * @example
 * import { initMarquee } from './utils/marquee.js';
 * initMarquee('.brand-marquee__track');
 */
export function initMarquee(trackSelector, options = {}) {
  const {
    speed = 0.4,
    prefersReducedMotion = true,
    autoFill = true,
    gap = null, // null 表示自动检测
  } = options;

  const track = typeof trackSelector === 'string'
    ? document.querySelector(trackSelector)
    : trackSelector;

  if (!track) {
    console.warn(`Marquee: Track element not found for selector "${trackSelector}"`);
    return null;
  }

  const marqueeRoot = track.closest('.brand-marquee, .supplementary-marquee') || track.parentElement;
  const originals = Array.from(track.children);
  
  if (originals.length === 0) {
    console.warn('Marquee: No children found in track');
    return null;
  }

  let offset = 0;
  let firstItem = track.firstElementChild;
  let firstWidth = firstItem ? firstItem.getBoundingClientRect().width : 0;
  let detectedGap = 0;
  let currentSpeed = speed;

  const prefersReducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

  // 根据屏幕宽度计算动态速度
  const calculateSpeed = () => {
    const baseSpeed = speed;
    const viewportWidth = window.innerWidth;
    // 1440px 以下保持基础速度，超过 1440px 后速度随屏幕宽度线性增加
    // 最大速度为基础速度的 3 倍（在 2560px 时）
    if (viewportWidth >= 1440) {
      const extraWidth = viewportWidth - 1440;
      const maxWidth = 2560; // 最大参考宽度
      const maxMultiplier = 3; // 最大速度倍数
      const speedMultiplier = 1 + (extraWidth / (maxWidth - 1440)) * (maxMultiplier - 1);
      return baseSpeed * Math.min(speedMultiplier, maxMultiplier);
    }
    return baseSpeed;
  };

  // 检测 gap
  const getGap = () => {
    if (gap !== null) return gap;
    const styles = window.getComputedStyle(track);
    const value = parseFloat(styles.columnGap || styles.gap || styles.gridColumnGap || '0');
    return Number.isFinite(value) ? value : 0;
  };

  const updateMeasurements = () => {
    detectedGap = getGap();
    firstItem = track.firstElementChild;
    firstWidth = firstItem ? firstItem.getBoundingClientRect().width : 0;
    currentSpeed = calculateSpeed();
  };

  const appendCloneSet = () => {
    const frag = document.createDocumentFragment();
    originals.forEach((source) => {
      const clone = source.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      frag.appendChild(clone);
    });
    track.appendChild(frag);
  };

  const ensureFill = () => {
    if (!autoFill || !marqueeRoot || !originals.length) return;
    const containerWidth = marqueeRoot.getBoundingClientRect().width || track.getBoundingClientRect().width;
    if (containerWidth === 0) return;
    
    const targetWidth = containerWidth * 2;
    let currentWidth = track.scrollWidth;
    let guard = 0;
    
    while (currentWidth < targetWidth) {
      appendCloneSet();
      currentWidth = track.scrollWidth;
      guard += 1;
      if (guard > 8) break; // 防止无限循环
    }
    
    updateMeasurements();
  };

  const handleResize = () => {
    ensureFill();
    offset = 0;
    track.style.transform = 'translateX(0)';
    updateMeasurements();
  };

  const tick = () => {
    const shouldAnimate = !prefersReducedMotion || !prefersReducedMotionMq.matches;
    
    if (shouldAnimate) {
      if (firstWidth <= 0) {
        updateMeasurements();
      }
      
      // 每帧更新速度（响应窗口大小变化）
      currentSpeed = calculateSpeed();
      offset -= currentSpeed;
      const threshold = firstWidth + detectedGap;
      
      if (threshold > 0 && -offset >= threshold) {
        offset += threshold;
        if (firstItem) {
          track.appendChild(firstItem);
          updateMeasurements();
        }
      }
      
      track.style.transform = `translateX(${offset}px)`;
    } else if (prefersReducedMotionMq.matches && offset !== 0) {
      offset = 0;
      track.style.transform = 'translateX(0)';
    }
    
    window.requestAnimationFrame(tick);
  };

  // 初始化
  ensureFill();
  updateMeasurements();
  
  // 事件监听
  window.addEventListener('resize', handleResize);
  window.addEventListener('load', handleResize);
  
  if (typeof prefersReducedMotionMq.addEventListener === 'function') {
    prefersReducedMotionMq.addEventListener('change', handleResize);
  } else if (typeof prefersReducedMotionMq.addListener === 'function') {
    prefersReducedMotionMq.addListener(handleResize);
  }
  
  // 启动动画
  window.requestAnimationFrame(tick);

  // 返回控制对象
  return {
    pause: () => {
      // 可以添加暂停功能
    },
    resume: () => {
      // 可以添加恢复功能
    },
    destroy: () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', handleResize);
      if (typeof prefersReducedMotionMq.removeEventListener === 'function') {
        prefersReducedMotionMq.removeEventListener('change', handleResize);
      } else if (typeof prefersReducedMotionMq.removeListener === 'function') {
        prefersReducedMotionMq.removeListener(handleResize);
      }
      track.style.transform = '';
    },
  };
}

/**
 * 批量初始化多个 marquee
 * @param {string|NodeList|Array} selectors - 选择器或元素列表
 * @param {Object} options - 配置选项
 */
export function initMarquees(selectors, options = {}) {
  const elements = typeof selectors === 'string'
    ? document.querySelectorAll(selectors)
    : Array.isArray(selectors) || selectors instanceof NodeList
      ? Array.from(selectors)
      : [selectors];

  return elements.map((el) => {
    const selector = typeof el === 'string' ? el : null;
    return initMarquee(selector || el, options);
  }).filter(Boolean);
}
